import { useCallback, useEffect, useRef, useState } from 'react';

const RenderCompareModal = ({
  open = false,
  leftSrc = '',
  rightSrc = '',
  leftLabel = 'Render 1',
  rightLabel = 'Render 2',
  onClose,
}) => {
  const containerRef = useRef(null);
  const [sliderPosition, setSliderPosition] = useState(50);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (open) setSliderPosition(50);
  }, [open, leftSrc, rightSrc]);

  const updateSliderFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0) return;
    const next = ((clientX - rect.left) / rect.width) * 100;
    setSliderPosition(Math.max(0, Math.min(100, next)));
  }, []);

  const startDrag = useCallback(
    (clientX) => {
      updateSliderFromClientX(clientX);
      const onMove = (event) => updateSliderFromClientX(event.clientX);
      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [updateSliderFromClientX],
  );

  if (!open || !leftSrc?.trim() || !rightSrc?.trim()) return null;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Compare renders"
      onClick={() => onClose?.()}
    >
      <div
        className="relative flex max-h-[92vh] w-full max-w-[min(92vw,960px)] flex-col rounded-2xl border border-white/20 bg-white p-3 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => onClose?.()}
          className="absolute right-3 top-3 z-20 flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 shadow-sm transition-colors hover:border-primary-300 hover:text-primary-300"
          title="Close compare"
          aria-label="Close compare"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M18 6L6 18" />
            <path d="M6 6l12 12" />
          </svg>
        </button>

        <div
          ref={containerRef}
          className="relative mx-auto mt-6 aspect-[4/3] w-full max-h-[75vh] cursor-ew-resize select-none touch-none overflow-hidden rounded-xl bg-gray-100"
          onMouseDown={(event) => {
            event.preventDefault();
            startDrag(event.clientX);
          }}
          onTouchStart={(event) => {
            const touch = event.touches?.[0];
            if (touch) updateSliderFromClientX(touch.clientX);
          }}
          onTouchMove={(event) => {
            const touch = event.touches?.[0];
            if (touch) updateSliderFromClientX(touch.clientX);
          }}
        >
          <img
            src={leftSrc}
            alt={leftLabel}
            className="absolute inset-0 h-full w-full object-contain"
            draggable={false}
            referrerPolicy="no-referrer"
          />
          <img
            src={rightSrc}
            alt={rightLabel}
            className="absolute inset-0 h-full w-full object-contain"
            draggable={false}
            referrerPolicy="no-referrer"
            style={{ clipPath: `inset(0 0 0 ${sliderPosition}%)` }}
          />
          <div
            className="pointer-events-none absolute top-0 w-0.5 bg-white shadow"
            style={{
              left: `calc(${sliderPosition}% - 1px)`,
              height: '100%',
            }}
          />
          <div
            className="pointer-events-none absolute flex h-7 w-7 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white bg-black/50 text-white"
            style={{ top: '50%', left: `${sliderPosition}%` }}
            aria-hidden
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M8 6L2 12l6 6" />
              <path d="M16 6l6 6-6 6" />
            </svg>
          </div>
          <span className="pointer-events-none absolute left-2 top-2 rounded bg-black/50 px-2 py-0.5 text-[10px] font-medium text-white">
            {leftLabel}
          </span>
          <span className="pointer-events-none absolute right-2 top-2 rounded bg-black/50 px-2 py-0.5 text-[10px] font-medium text-white">
            {rightLabel}
          </span>
        </div>
        <p className="mt-2 text-center text-xs text-gray-500">Drag to compare renders</p>
      </div>
    </div>
  );
};

export default RenderCompareModal;
